import {
  DEFAULT_LANGUAGE,
  LOGIN_SIGNUP_LANGUAGES,
  PUBLIC_INAPP_LANGUAGES,
  SUPPORTED_LANGUAGES,
} from "../../../config/i18n";
import type { SupportedLanguage } from "../../../interfaces";
import { resolveRoomLanguage } from "./langUtils";

const APP_PAGE_PREFIXES = [
  "/settings",
  "/profile",
  "/gpt",
  "/welcome",
  "/rooms",
  "/onboarding",
  "/discovery",
  "/search",
  "/post",
  "/playlist",
  "/notifications",
  // The visual-abstract screens of the signed-in app: the library
  // (`/visual-abstracts`), creation (`/visual-abstracts/new`), preview and report
  // (`/visual-abstract/:id/…`). Missing here, they fell to "public-content" and
  // followed the browser language instead of the reader's. The public poster
  // lives at `/v/:token` and is not matched.
  "/visual-abstract",
];

export type PageContext =
  | { kind: "login-signup" }
  | { kind: "public-content" }
  | { kind: "app-page" }
  | { kind: "room"; allowedLanguages: ReadonlyArray<SupportedLanguage> };

/** Which language rule a page follows, from its path and its room. */
export function getPageContext(
  pathname: string,
  currentRoom: { allowedLanguages: ReadonlyArray<SupportedLanguage> } | null
): PageContext {
  if (
    ["/login", "/signup", "/terms", "/policy"].some((p) =>
      pathname.startsWith(p)
    )
  ) {
    return { kind: "login-signup" };
  }
  if (currentRoom !== null) {
    return {
      kind: "room",
      allowedLanguages:
        currentRoom.allowedLanguages.length > 0
          ? currentRoom.allowedLanguages
          : [DEFAULT_LANGUAGE],
    };
  }
  if (APP_PAGE_PREFIXES.some((p) => pathname.startsWith(p))) {
    return { kind: "app-page" };
  }
  return { kind: "public-content" };
}

export type ResolveLangSyncInput = {
  pageContext: PageContext;
  userLanguage: SupportedLanguage | null;
  storedPublicAppLang: SupportedLanguage | null;
  navigatorLang: SupportedLanguage | null;
  roomOverride: SupportedLanguage | null;
};

function resolveContentPageLang(
  storedPublicAppLang: SupportedLanguage | null,
  navigatorLang: SupportedLanguage | null
): SupportedLanguage {
  if (
    storedPublicAppLang !== null &&
    PUBLIC_INAPP_LANGUAGES.includes(storedPublicAppLang)
  ) {
    return storedPublicAppLang;
  }
  if (
    navigatorLang !== null &&
    PUBLIC_INAPP_LANGUAGES.includes(navigatorLang)
  ) {
    return navigatorLang;
  }
  return DEFAULT_LANGUAGE;
}

/**
 * Pure function that determines which language LangSync should switch to.
 * Returns the target language, or null if LangSync should hold off
 * (e.g. waiting for user data to load).
 */
export function resolveLangSync(
  input: ResolveLangSyncInput
): SupportedLanguage | null {
  const {
    pageContext,
    userLanguage,
    storedPublicAppLang,
    navigatorLang,
    roomOverride,
  } = input;

  switch (pageContext.kind) {
    case "app-page": {
      if (userLanguage === null) return null;
      return SUPPORTED_LANGUAGES.includes(userLanguage)
        ? userLanguage
        : DEFAULT_LANGUAGE;
    }

    case "login-signup": {
      if (
        storedPublicAppLang !== null &&
        LOGIN_SIGNUP_LANGUAGES.includes(storedPublicAppLang)
      ) {
        return storedPublicAppLang;
      }
      if (
        navigatorLang !== null &&
        LOGIN_SIGNUP_LANGUAGES.includes(navigatorLang)
      ) {
        return navigatorLang;
      }
      return DEFAULT_LANGUAGE;
    }

    case "public-content": {
      return resolveContentPageLang(storedPublicAppLang, navigatorLang);
    }

    case "room": {
      if (userLanguage === null) return null;
      // Always do the full two-tier resolution. The caller (LangSync)
      // protects in-session picker changes via a ref-based skip — the
      // pure function doesn't need to preserve currentLang.
      const { language } = resolveRoomLanguage({
        userLanguage,
        allowedLanguages: pageContext.allowedLanguages,
        roomOverride,
      });
      return language;
    }

    default: {
      const exhaustive: never = pageContext;
      throw new Error(
        `Unexpected page context: ${(exhaustive as PageContext).kind}`
      );
    }
  }
}
